import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  useMediaQuery,
  Tooltip,
  Badge,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditNoteIcon from '@mui/icons-material/EditNote';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import HomeIcon from '@mui/icons-material/Home';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PaymentIcon from '@mui/icons-material/Payment';
import QuizIcon from '@mui/icons-material/Quiz';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import MyTermsListDialog from './study/MyTermsListDialog';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { ensureValidSession } from '../utils/auth';

const Header = ({
  isLoggedIn,
  isAdmin,
  user,
  onLogout,
  onLoginClick,
  onSignupClick,
  onWrongAnswersClick,
  wrongAnswersCount,
  isLandingPage
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAdmin: authIsAdmin, userProfile } = useAuth();
  const { 
    setOpenAddMyTermDialog, 
    setOpenAddMyDefinitionDialog,
    openMyTermsList,
    setOpenMyTermsList,
    darkMode,
    setDarkMode,
    setOpenSettings
  } = useApp();
  
  // 모바일 화면 감지
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [termMenuAnchorEl, setTermMenuAnchorEl] = useState(null);
  const [bllExpiry, setBllExpiry] = useState(null);
  
  const mobileMenuOpen = Boolean(mobileMenuAnchorEl);
  const userMenuOpen = Boolean(userMenuAnchorEl);
  const termMenuOpen = Boolean(termMenuAnchorEl);
  
  // 모바일 메뉴 열기/닫기
  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };
  
  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };
  
  // 사용자 메뉴 열기/닫기
  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };
  
  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };
  
  // 용어 관리 메뉴 열기/닫기
  const handleTermMenuOpen = (event) => {
    setTermMenuAnchorEl(event.currentTarget);
  };
  
  const handleTermMenuClose = () => {
    setTermMenuAnchorEl(null);
  };
  
  // 메뉴 항목 클릭 핸들러
  const handleMenuItemClick = () => {
    console.log('Header: Menu item clicked');
    handleUserMenuClose();
    handleMobileMenuClose();
    handleTermMenuClose();
  };
  
  const handleLoginMenuClick = () => {
    console.log('Header: Login menu item clicked');
    handleMenuItemClick();
    if (onLoginClick) {
      onLoginClick();
    }
  };
  
  const handleSignupMenuClick = () => {
    console.log('Header: Signup menu item clicked');
    handleMenuItemClick();
    if (onSignupClick) {
      onSignupClick();
    }
  };
  
  // My own Term 다이얼로그 열기
  const handleOpenMyTermDialog = () => {
    setOpenAddMyTermDialog(true);
    handleTermMenuClose();
  };
  
  // My own Definition 다이얼로그 열기
  const handleOpenMyDefinitionDialog = () => {
    setOpenAddMyDefinitionDialog(true);
    handleTermMenuClose();
  };
  
  const handleOpenMyTermsList = () => {
    setOpenMyTermsList(true);
    handleTermMenuClose();
  };
  
  // 사용자 이름 및 이메일 가져오기
  const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || '사용자';
  const userEmail = user?.email || '';
  const userAvatar = user?.user_metadata?.avatar_url;
  
  // 그라데이션 스타일
  const gradientTextStyle = {
    background: 'linear-gradient(45deg, #3f51b5 30%, #f50057 90%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: 700
  };
  
  // 로그인 버튼 스타일
  const loginButtonStyle = {
    borderRadius: '20px', 
    fontWeight: 600,
    px: 2
  };
  
  // 회원가입 버튼 스타일
  const signupButtonStyle = {
    borderRadius: '20px',
    fontWeight: 600,
    background: 'linear-gradient(45deg, #3f51b5 30%, #f50057 90%)',
    boxShadow: '0 3px 5px 2px rgba(63, 81, 181, .3)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(45deg, #303f9f 30%, #d5004d 90%)',
      boxShadow: '0 4px 6px 2px rgba(63, 81, 181, .3)',
    },
    px: 2
  };
  
  const handlePricingClick = () => {
    handleMobileMenuClose();
    handleMenuItemClick();
    navigate('/pricing');
  };
  
  // 로그인된 사용자의 요금제 메뉴 클릭 핸들러
  const handleLoggedInPricingClick = () => {
    handleUserMenuClose();
    handleMenuItemClick();
    navigate('/pricing');
  };

  const handleSettingsClick = () => {
    handleMenuItemClick();
    setOpenSettings(true);
  };
  
  const handleProfileClick = () => {
    handleMenuItemClick();
    navigate('/settings/profile');
  };
  
  // 모바일 메뉴의 설정 클릭 핸들러도 수정
  const handleMobileSettingsClick = () => {
    handleMobileMenuClose();
    setOpenSettings(true);
  };
  
  const handleLogoutClick = async () => {
    handleUserMenuClose();
    handleMenuItemClick();
    await logout();
    navigate('/');
  };
  
  const handleThemeToggle = () => {
    setDarkMode(!darkMode);
    localStorage.setItem('darkMode', !darkMode);
  };
  
  // 현재 경로가 MPT 페이지인지 확인
  const isMptPage = location.pathname === '/mpt_test';
  
  useEffect(() => {
    async function fetchBllExpiry() {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return;
      const { data } = await supabase
        .from('profiles')
        .select('subscription_expiry')
        .eq('id', userId)
        .single();
      setBllExpiry(data?.subscription_expiry || null);
    }
    fetchBllExpiry();
  }, []);

  useEffect(() => {
    if (user) {
      const sessionCheckInterval = setInterval(async () => {
        const session = await ensureValidSession();
        if (!session) {
          console.log('세션이 만료되었습니다. 로그아웃 처리합니다.');
          logout();
        }
      }, 5 * 60 * 1000); // 5분마다 확인

      return () => clearInterval(sessionCheckInterval);
    }
  }, [user, logout]);

  const getRemainDays = (expiry) => {
    if (!expiry) return null;
    const today = new Date();
    const end = new Date(expiry);
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const remainDays = getRemainDays(bllExpiry);
  
  return (
    <>
      <AppBar position="static" sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Toolbar>
          {/* 로고 및 타이틀 */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography
              variant="h5"
              component={RouterLink}
              to="/"
              sx={{
                fontFamily: '"Pacifico", cursive',
                fontSize: { xs: '1.8rem', md: '2.2rem' },
                background: 'linear-gradient(45deg, #3f51b5 30%, #f50057 90%)',
                backgroundSize: '200% auto',
                animation: 'gradient 3s ease infinite',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                transition: 'transform 0.2s ease-in-out',
                '@keyframes gradient': {
                  '0%': {
                    backgroundPosition: '0% 50%'
                  },
                  '50%': {
                    backgroundPosition: '100% 50%'
                  },
                  '100%': {
                    backgroundPosition: '0% 50%'
                  }
                },
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
            >
              BLL Pro
            </Typography>
          </Box>

          {/* 비로그인 상태의 데스크탑 메뉴 */}
          {!isMobile && !isLoggedIn && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handlePricingClick}
                variant="outlined"
                sx={{
                  ...loginButtonStyle,
                  color: 'text.primary',
                  '&:hover': {
                    background: 'rgba(63, 81, 181, 0.04)',
                    borderColor: 'primary.main'
                  }
                }}
              >
                요금제
              </Button>
              <Button
                variant="outlined"
                onClick={handleLoginMenuClick}
                startIcon={<LoginIcon />}
                sx={loginButtonStyle}
              >
                로그인
              </Button>
              <Button
                variant="contained"
                onClick={handleSignupMenuClick}
                startIcon={<PersonAddIcon />}
                sx={signupButtonStyle}
              >
                회원가입
              </Button>
            </Box>
          )}

          {/* 데스크탑 메뉴 */}
          {!isMobile && isLoggedIn && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isLandingPage && location.pathname !== '/pricing' && (
                <>
                  <Button
                    component={RouterLink}
                    to={isMptPage ? "/" : "/mpt_test"}
                    variant="contained"
                    sx={{
                      borderRadius: '20px',
                      fontWeight: 600,
                      px: 2,
                      backgroundColor: '#4B48DF',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#3f3dbf',
                      }
                    }}
                  >
                    {isMptPage ? "BLL Pro" : "MPT 실전테스트"}
                  </Button>
                  <Button
                    component={RouterLink}
                    to={location.pathname === '/mee_test' ? "/" : "/mee_test"}
                    variant="contained"
                    sx={{
                      borderRadius: '20px',
                      fontWeight: 600,
                      px: 2,
                      backgroundColor: '#4B48DF',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#3f3dbf',
                      },
                      ml: 1
                    }}
                  >
                    {location.pathname === '/mee_test' ? "BLL Pro" : "MEE 실전테스트"}
                  </Button>
                  <Button
                    component={RouterLink}
                    to={location.pathname === '/mbe_test' ? "/" : "/mbe_test"}
                    variant="contained"
                    sx={{
                      borderRadius: '20px',
                      fontWeight: 600,
                      px: 2,
                      backgroundColor: '#4B48DF',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#3f3dbf',
                      },
                      ml: 1
                    }}
                  >
                    {location.pathname === '/mbe_test' ? "BLL Pro" : "MBE 실전테스트"}
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/my_review"
                    variant="contained"
                    color="secondary"
                    startIcon={<CompareArrowsIcon />}
                    sx={{
                      borderRadius: '20px',
                      fontWeight: 600,
                      px: 2,
                      backgroundColor: '#ef476f',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#b91c4a',
                      },
                      ml: 1
                    }}
                  >
                    복습/비교
                  </Button>
                </>
              )}
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate('/community')}
                sx={{ fontWeight: 600, borderRadius: '20px', px: 2 }}
              >
                커뮤니티로 이동
              </Button>
              <Tooltip title="용어 관리">
                <IconButton
                  color="inherit"
                  onClick={handleTermMenuOpen}
                  sx={{ color: 'text.primary' }}
                >
                  <BookmarkAddIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="나의 학습현황">
                <IconButton
                  color="inherit"
                  onClick={onWrongAnswersClick}
                  sx={{ color: 'text.primary' }}
                >
                  <Badge badgeContent={wrongAnswersCount} color="error">
                    <AnalyticsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              
              <Tooltip title="설정">
                <IconButton
                  color="inherit"
                  onClick={handleSettingsClick}
                  sx={{ color: 'text.primary' }}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>

              <Tooltip
                title={
                  bllExpiry
                    ? (remainDays > 0
                        ? `BLL 학습권/정기권 만료일: ${bllExpiry.slice(0, 10)} (D-${remainDays})`
                        : 'BLL 학습권/정기권이 만료되었습니다. 재구매 필요')
                    : 'BLL 학습권/정기권 미보유'
                }
              >
                <span style={{
                  fontSize: 20,
                  marginLeft: 8,
                  color: bllExpiry && remainDays > 0 ? '#1976d2' : '#888',
                  cursor: 'pointer'
                }}>
                  📅
                </span>
              </Tooltip>

              <Tooltip title="계정">
                <IconButton
                  onClick={handleUserMenuOpen}
                  sx={{ color: 'text.primary', ml: 1 }}
                >
                  <Avatar
                    src={userAvatar}
                    alt={userName}
                    sx={{ width: 32, height: 32 }}
                  >
                    <AccountCircleIcon />
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* 모바일 메뉴 아이콘 */}
          {isMobile && !isLoggedIn && (
            <>
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleMobileMenuOpen}
                sx={{ color: 'text.primary' }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchorEl}
                open={mobileMenuOpen}
                onClose={handleMobileMenuClose}
              >
                <MenuItem onClick={handlePricingClick}>
                  <ListItemIcon><PaymentIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>요금제</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleLoginMenuClick}>
                  <ListItemIcon><LoginIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>로그인</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleSignupMenuClick}>
                  <ListItemIcon><PersonAddIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>회원가입</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}

          {/* 용어 관리 메뉴 */}
          <Menu
            anchorEl={termMenuAnchorEl}
            id="term-menu"
            keepMounted
            open={termMenuOpen}
            onClose={handleTermMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              elevation: 3,
              sx: { width: 250, borderRadius: 2, mt: 1.5 }
            }}
          >
            {/* 확인 섹션 */}
            <MenuItem onClick={handleOpenMyTermsList}>
              <ListItemIcon>
                <BookmarkAddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="나의 학습 노트 보기" secondary="용어/정의 목록 확인" />
            </MenuItem>
            <Divider />
            {/* 입력 섹션 */}
            <Box sx={{ px: 2, py: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                새로운 항목 추가
              </Typography>
            </Box>
            <MenuItem onClick={handleOpenMyTermDialog}>
              <ListItemIcon>
                <NoteAddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="용어 추가" secondary="나만의 용어 입력" />
            </MenuItem>
            <MenuItem onClick={handleOpenMyDefinitionDialog}>
              <ListItemIcon>
                <EditNoteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="정의 추가" secondary="나만의 정의 입력" />
            </MenuItem>
          </Menu>

          {/* 모바일 메뉴 */}
          <Menu
            anchorEl={mobileMenuAnchorEl}
            id="mobile-menu"
            keepMounted
            open={mobileMenuOpen}
            onClose={handleMobileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              elevation: 3,
              sx: { width: 250, borderRadius: 2, mt: 1.5 }
            }}
          >
            {isLoggedIn ? (
              <>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Avatar 
                      src={userAvatar}
                      alt={userName}
                      sx={{ width: 40, height: 40, mx: 'auto', mb: 1 }}
                    />
                    <Typography variant="subtitle1" fontWeight={600}>{userName}</Typography>
                    <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                      {userEmail}
                    </Typography>
                  </Box>

                  {/* 요금제 정보 */}
                  {user?.subscription_tier && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        현재 요금제
                      </Typography>
                      <Chip
                        label={user.subscription_tier === 'premium' ? '프리미엄' : 
                               user.subscription_tier === 'team' ? '팀' : '무료 체험'}
                        size="small"
                        color={user.subscription_tier === 'premium' ? 'primary' : 
                               user.subscription_tier === 'team' ? 'secondary' : 'default'}
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          width: '100%'
                        }}
                      />
                    </Box>
                  )}
                </Box>
                <Divider />
                <MenuItem 
                  component={RouterLink} 
                  to="/"
                  onClick={() => handleMenuItemClick()}
                >
                  <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>홈</ListItemText>
                </MenuItem>
                
                {location.pathname !== '/pricing' && (
                  <>
                    <MenuItem 
                      component={RouterLink}
                      to={isMptPage ? "/" : "/mpt_test"}
                      onClick={() => handleMenuItemClick()}
                    >
                      <ListItemIcon><QuizIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>{isMptPage ? "BLL Pro" : "MPT 실전테스트"}</ListItemText>
                    </MenuItem>
                    <MenuItem 
                      component={RouterLink}
                      to={location.pathname === '/mee_test' ? "/" : "/mee_test"}
                      onClick={() => handleMenuItemClick()}
                    >
                      <ListItemIcon><QuizIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>{location.pathname === '/mee_test' ? "BLL Pro" : "MEE 실전테스트"}</ListItemText>
                    </MenuItem>
                    <MenuItem 
                      component={RouterLink}
                      to={location.pathname === '/mbe_test' ? "/" : "/mbe_test"}
                      onClick={() => handleMenuItemClick()}
                    >
                      <ListItemIcon><QuizIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>{location.pathname === '/mbe_test' ? "BLL Pro" : "MBE 실전테스트"}</ListItemText>
                    </MenuItem>
                  </>
                )}
                
                {/* 확인 섹션 */}
                <Box sx={{ px: 2, py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    나의 학습 노트
                  </Typography>
                </Box>
                <MenuItem onClick={handleOpenMyTermsList}>
                  <ListItemIcon><BookmarkAddIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="용어/정의 목록 보기" />
                </MenuItem>
                
                {/* 입력 섹션 */}
                <Box sx={{ px: 2, py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    새로운 항목 추가
                  </Typography>
                </Box>
                <MenuItem onClick={handleOpenMyTermDialog}>
                  <ListItemIcon><NoteAddIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="용어 추가" secondary="나만의 용어 입력" />
                </MenuItem>
                <MenuItem onClick={handleOpenMyDefinitionDialog}>
                  <ListItemIcon><EditNoteIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="정의 추가" secondary="나만의 정의 입력" />
                </MenuItem>
                <Divider />
                
                <MenuItem onClick={() => handleMenuItemClick(onWrongAnswersClick)}>
                  <ListItemIcon>
                    <Badge badgeContent={wrongAnswersCount} color="error">
                      <AnalyticsIcon fontSize="small" />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText>나의 학습현황</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleSettingsClick}>
                  <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>설정</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleProfileClick}>
                  <ListItemIcon>
                    <AccountCircleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="프로필" />
                </MenuItem>
                <MenuItem onClick={handleLogoutClick}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>로그아웃</ListItemText>
                </MenuItem>
              </>
            ) : (
              <>
                <MenuItem 
                  component={RouterLink} 
                  to="/"
                  onClick={() => handleMenuItemClick()}
                >
                  <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>홈</ListItemText>
                </MenuItem>
                {location.pathname !== '/pricing' && (
                  <>
                    <MenuItem 
                      component={RouterLink}
                      to={isMptPage ? "/" : "/mpt_test"}
                      onClick={() => handleMenuItemClick()}
                    >
                      <ListItemIcon><QuizIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>{isMptPage ? "BLL Pro" : "MPT 실전테스트"}</ListItemText>
                    </MenuItem>
                    <MenuItem 
                      component={RouterLink}
                      to={location.pathname === '/mee_test' ? "/" : "/mee_test"}
                      onClick={() => handleMenuItemClick()}
                    >
                      <ListItemIcon><QuizIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>{location.pathname === '/mee_test' ? "BLL Pro" : "MEE 실전테스트"}</ListItemText>
                    </MenuItem>
                    <MenuItem 
                      component={RouterLink}
                      to={location.pathname === '/mbe_test' ? "/" : "/mbe_test"}
                      onClick={() => handleMenuItemClick()}
                    >
                      <ListItemIcon><QuizIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>{location.pathname === '/mbe_test' ? "BLL Pro" : "MBE 실전테스트"}</ListItemText>
                    </MenuItem>
                  </>
                )}
                <MenuItem onClick={handlePricingClick}>
                  <ListItemIcon><PaymentIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>요금제</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleLoginMenuClick}>
                  <ListItemIcon><LoginIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>로그인</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleSignupMenuClick}>
                  <ListItemIcon><PersonAddIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>회원가입</ListItemText>
                </MenuItem>
              </>
            )}
          </Menu>

          {/* 사용자 메뉴 */}
          <Menu
            anchorEl={userMenuAnchorEl}
            open={userMenuOpen}
            onClose={handleUserMenuClose}
            onClick={handleMenuItemClick}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleProfileClick}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="프로필" />
            </MenuItem>

            <MenuItem onClick={onWrongAnswersClick}>
              <ListItemIcon>
                <Badge badgeContent={wrongAnswersCount} color="error">
                  <AnalyticsIcon fontSize="small" />
                </Badge>
              </ListItemIcon>
              <ListItemText primary="나의 학습현황" />
            </MenuItem>

            <MenuItem onClick={handleSettingsClick}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="설정" />
            </MenuItem>

            <Divider />

            <MenuItem onClick={handleLoggedInPricingClick}>
              <ListItemIcon>
                <PaymentIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="요금제" />
            </MenuItem>

            <MenuItem onClick={handleLogoutClick}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="로그아웃" />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* 나만의 용어/정의 목록 다이얼로그 */}
      <MyTermsListDialog
        open={openMyTermsList}
        onClose={() => setOpenMyTermsList(false)}
      />
    </>
  );
};

export default Header;